import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from './AppIcon';
import Button from './ui/Button';
import ChatHeader from '../pages/ai-assistant-interface-intelligent-support/components/ChatHeader';
import WelcomeMessage from '../pages/ai-assistant-interface-intelligent-support/components/WelcomeMessage';
import ConversationArea from '../pages/ai-assistant-interface-intelligent-support/components/ConversationArea';
import ChatInput from '../pages/ai-assistant-interface-intelligent-support/components/ChatInput';
import QuickActions from '../pages/ai-assistant-interface-intelligent-support/components/QuickActions';

const GlobalAIChat = () => {
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [messages, setMessages] = useState([]);
    const [isTyping, setIsTyping] = useState(false);
    const [showWelcome, setShowWelcome] = useState(true);

    const handleSendMessage = async (messageContent) => {
        if (!messageContent.trim()) return;

        const userMessage = {
            content: messageContent,
            isUser: true,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setShowWelcome(false);
        setIsTyping(true);

        try {
            const response = await fetch('https://api.groq.com/openai/v1/chat/completions', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${import.meta.env.VITE_GROQ_API_KEY}`
                },
                body: JSON.stringify({
                    model: 'llama-3.3-70b-versatile',
                    messages: [
                        {
                            role: 'system',
                            content: `You are a helpful AI assistant for Byte Pairs Digital Solutions, a web development company. 
              
Company Information:
- Email: bytepaire@gmail.com
- Phone: +91 9003241548
- Location: Dharapuram, Tamil Nadu, India
- Services: Web Development, Mobile Apps, E-commerce, Custom Software
- Technologies: React, Next.js, Node.js, Python, React Native, Flutter
- Pricing: Starter ($2,999), Professional ($7,999), Enterprise ($15,999+)

Be friendly, professional, and helpful. Provide accurate information about the company's services, pricing, and capabilities. If asked about scheduling, direct them to contact via email or phone.`
                        },
                        ...messages.map(msg => ({
                            role: msg.isUser ? 'user' : 'assistant',
                            content: msg.content
                        })),
                        {
                            role: 'user',
                            content: messageContent
                        }
                    ],
                    temperature: 0.7,
                    max_tokens: 1024
                })
            });

            if (!response.ok) {
                throw new Error('Failed to get AI response');
            }

            const data = await response.json();
            const aiResponse = data.choices[0]?.message?.content || 'Sorry, I encountered an error. Please try again.';

            const assistantMessage = {
                content: aiResponse,
                isUser: false,
                timestamp: new Date()
            };

            setMessages(prev => [...prev, assistantMessage]);
        } catch (error) {
            console.error('Error calling Groq API:', error);
            const errorMessage = {
                content: 'Sorry, I\'m having trouble connecting right now. Please try again in a moment or contact us directly at bytepaire@gmail.com or +91 9003241548.',
                isUser: false,
                timestamp: new Date()
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsTyping(false);
        }
    };

    const handleStartConversation = (initialMessage) => {
        setShowWelcome(false);
        handleSendMessage(initialMessage);
    };

    const handleQuickAction = (message) => {
        handleSendMessage(message);
    };

    const openChat = () => {
        setIsChatOpen(true);
        setIsMinimized(false);
    };

    const closeChat = () => {
        setIsChatOpen(false);
        setIsMinimized(false);
    };

    const toggleMinimize = () => {
        setIsMinimized(!isMinimized);
    };

    return (
        <>
            {/* Floating Chat Button */}
            <AnimatePresence>
                {!isChatOpen && (
                    <motion.div
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed bottom-6 right-6 z-50"
                    >
                        <Button
                            variant="default"
                            size="icon"
                            onClick={openChat}
                            className="w-16 h-16 bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 shadow-[0_0_30px_rgba(234,179,8,0.4)] hover:shadow-[0_0_40px_rgba(234,179,8,0.6)] transition-all duration-300 hover:scale-110 rounded-full"
                        >
                            <Icon name="Bot" size={28} color="black" />
                        </Button>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Chat Interface */}
            <AnimatePresence>
                {isChatOpen && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8, y: 20 }}
                        animate={{
                            opacity: 1,
                            scale: 1,
                            y: 0,
                            height: isMinimized ? 'auto' : '720px'
                        }}
                        exit={{ opacity: 0, scale: 0.8, y: 20 }}
                        transition={{ duration: 0.3 }}
                        className="fixed bottom-6 right-6 w-[480px] max-w-[calc(100vw-3rem)] bg-background border border-border rounded-lg shadow-2xl z-50 overflow-hidden"
                    >
                        <ChatHeader
                            onClose={closeChat}
                            onMinimize={toggleMinimize}
                            isMinimized={isMinimized}
                        />

                        {!isMinimized && (
                            <>
                                {showWelcome ? (
                                    <WelcomeMessage onStartConversation={handleStartConversation} />
                                ) : (
                                    <div className="flex flex-col h-[650px]">
                                        <ConversationArea
                                            messages={messages}
                                            isTyping={isTyping}
                                        />

                                        <div className="border-t border-border bg-background">
                                            <ChatInput
                                                onSendMessage={handleSendMessage}
                                                disabled={isTyping}
                                                placeholder="Ask me anything about bytepairs' services..."
                                            />

                                            <QuickActions
                                                onActionClick={handleQuickAction}
                                                disabled={isTyping}
                                            />
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default GlobalAIChat;
